import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload as UploadIcon, 
  FileText, 
  Download, 
  ChevronRight, 
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { collection, addDoc, serverTimestamp } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';

interface EmployeeImportProps {
  onClose: () => void;
  onComplete: () => void;
  orgId: string;
}

export default function EmployeeImport({ onClose, onComplete, orgId }: EmployeeImportProps) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mappedData, setMappedData] = useState<any[]>([]);
  const [importProgress, setImportProgress] = useState({ completed: 0, total: 0 });
  const [importError, setImportError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!file) return;
    setIsUploading(true);
    // Simulate parsing
    setTimeout(() => {
      setIsUploading(false);
      // Dummy data for preview
      setMappedData([
        { firstName: 'John', lastName: 'Doe', email: 'john@example.com', jobTitle: 'Engineer', department: 'IT', salary: 5000 },
        { firstName: 'Jane', lastName: 'Smith', email: 'jane@example.com', jobTitle: 'Designer', department: 'Creative', salary: 4500 },
      ]);
      setStep(2);
    }, 1500);
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'First Name', 'Last Name', 'Email', 'Phone', 'Job Title', 
      'Department', 'Status', 'Joining Date', 'Salary', 
      'Payment Frequency', 'Bank Name', 'Account Number', 'Notes'
    ];
    const csvContent = [
      headers.join(','),
      'John,Doe,john@example.com,+123456789,Software Engineer,Engineering,Active,2026-01-01,5000,Monthly,Global Bank,1234567890,Initial hire',
      'Jane,Smith,jane@example.com,+198765432,Product Designer,Design,Active,2026-02-15,4500,Monthly,City Bank,0987654321,Senior designer'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'employee_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFinalImport = async () => {
    const localAuthToken = typeof window !== 'undefined'
      ? window.localStorage.getItem('app_auth_token')
      : null;

    setIsImporting(true);
    setImportError(null);
    setImportProgress({ completed: 0, total: mappedData.length });

    try {
      if (localAuthToken) {
        for (let index = 0; index < mappedData.length; index += 1) {
          const emp = mappedData[index];
          const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/employees`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localAuthToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...emp,
              status: emp.status || 'Active',
              joiningDate: emp.joiningDate || new Date().toISOString().split('T')[0],
              paymentFrequency: emp.paymentFrequency || 'Monthly',
            }),
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            throw new Error(payload?.error || `Failed to import employee ${index + 1} (${response.status})`);
          }

          setImportProgress({ completed: index + 1, total: mappedData.length });
        }
      } else {
        // In a real app, we'd iterate over parsed CSV data.
        // For this demo, we'll add the dummy data.
        for (let index = 0; index < mappedData.length; index += 1) {
          const emp = mappedData[index];
          await addDoc(collection(db, 'employees'), {
            ...emp,
            orgId,
            status: emp.status || 'Active',
            joiningDate: emp.joiningDate || new Date().toISOString().split('T')[0],
            paymentFrequency: emp.paymentFrequency || 'Monthly',
            createdBy: auth.currentUser?.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          setImportProgress({ completed: index + 1, total: mappedData.length });
        }
      }

      notify.confirm(`Successfully imported ${mappedData.length} employees`);
      onComplete();
    } catch (error) {
      console.error("Import error:", error);
      const message = error instanceof Error ? error.message : 'Failed to import employees';
      setImportError(message);
      notify.error(message);

      if (!localAuthToken) {
        handleFirestoreError(error, OperationType.CREATE, 'employees');
      }
    } finally {
      setIsImporting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-4 mb-12">
      {[1, 2].map((s) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
              step === s ? "bg-[#141414] text-[#E4E3E0]" : 
              step > s ? "bg-green-500 text-white" : "bg-[#141414]/10 text-[#141414]/40"
            )}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            <span className={cn(
              "text-sm font-bold",
              step === s ? "opacity-100" : "opacity-40"
            )}>
              {s === 1 ? 'Upload' : 'Review'}
            </span>
          </div>
          {s < 2 && <div className="w-12 h-[2px] bg-[#141414]/10" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl rounded-[40px] p-12 shadow-2xl relative overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
        <button 
          onClick={onClose}
          className="absolute top-8 right-8 p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {renderStepIndicator()}

        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div>
                <h2 className="text-4xl font-bold tracking-tight mb-4">Import Employees</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed max-w-2xl">
                  Upload a CSV file containing your employee records. Use our template to ensure correct formatting.
                </p>
              </div>

              <div className="flex justify-center">
                <button 
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 border border-[#141414] px-6 py-3 rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold"
                >
                  <Download className="w-4 h-4" />
                  <span>Download CSV Template</span>
                </button>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">File to upload</h3>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed border-[#141414]/20 rounded-3xl p-12 text-center cursor-pointer hover:border-[#141414] hover:bg-white/20 transition-all group relative",
                    file && "border-green-500 bg-green-50/50"
                  )}
                >
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    className="hidden" 
                    accept=".csv"
                    onChange={handleFileSelect}
                  />
                  {!file ? (
                    <>
                      <div className="w-16 h-16 bg-[#141414]/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <UploadIcon className="w-8 h-8 opacity-40" />
                      </div>
                      <p className="text-xl font-bold italic serif mb-2">Drag and drop CSV file or select manually</p>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        className="bg-[#141414] text-[#E4E3E0] px-8 py-3 rounded-full font-bold mt-4 hover:opacity-90 transition-opacity relative z-10"
                      >
                        Select file
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-4">
                      <div className="p-4 bg-green-100 text-green-600 rounded-2xl">
                        <FileText className="w-8 h-8" />
                      </div>
                      <div className="text-left">
                        <p className="text-lg font-bold">{file.name}</p>
                        <p className="text-sm opacity-50">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="ml-4 p-2 hover:bg-red-100 text-red-500 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button 
                  disabled={!file || isUploading}
                  onClick={handleUpload}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity disabled:opacity-30 flex items-center gap-2"
                >
                  {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h2 className="text-4xl font-bold tracking-tight mb-4">Review employees</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed">
                  We found {mappedData.length} employees in your file. Review them before importing.
                </p>
              </div>

              <div className="bg-white/30 border border-[#141414] rounded-3xl overflow-hidden overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#141414] text-[#E4E3E0] uppercase text-[10px] tracking-widest font-bold sticky top-0 z-10">
                      <th className="px-6 py-4">Name</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Job Title</th>
                      <th className="px-6 py-4">Department</th>
                      <th className="px-6 py-4 text-right">Salary</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#141414]/10">
                    {mappedData.map((emp, i) => (
                      <tr key={i} className="hover:bg-white/20 transition-colors">
                        <td className="px-6 py-4 text-sm font-bold">{emp.firstName} {emp.lastName}</td>
                        <td className="px-6 py-4 text-sm">{emp.email}</td>
                        <td className="px-6 py-4 text-sm">{emp.jobTitle}</td>
                        <td className="px-6 py-4 text-sm">{emp.department}</td>
                        <td className="px-6 py-4 text-right text-sm font-bold font-mono">
                          {(emp.salary || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-6 rounded-3xl flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-blue-600 shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-bold text-blue-900">Ready to import</p>
                  <p className="text-sm text-blue-800 opacity-80 italic serif">
                    These employees will be added to your organization. You can manage their payroll details after import.
                  </p>
                </div>
              </div>

              {(isImporting || importError) && (
                <div
                  className={cn(
                    "border p-4 rounded-2xl",
                    importError ? "bg-red-50 border-red-200 text-red-800" : "bg-[#141414]/5 border-[#141414]/10"
                  )}
                  aria-live="polite"
                >
                  {isImporting && (
                    <>
                      <div className="flex items-center justify-between gap-4 text-sm font-bold">
                        <span>Importing employees</span>
                        <span>{importProgress.completed} of {importProgress.total}</span>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#141414]/10">
                        <div
                          className="h-full bg-[#141414] transition-[width] duration-200"
                          style={{ width: `${importProgress.total ? (importProgress.completed / importProgress.total) * 100 : 0}%` }}
                        />
                      </div>
                    </>
                  )}
                  {importError && (
                    <p className={cn("text-sm", isImporting && "mt-3")}>
                      Import stopped after {importProgress.completed} of {importProgress.total} employees: {importError}
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-between pt-4">
                <button 
                  onClick={() => setStep(1)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Back
                </button>
                <button 
                  disabled={isImporting}
                  onClick={handleFinalImport}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isImporting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Complete Import'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
